
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import {
  download_file,
  convert_seconds_to_minutes,
  parseStrToInt,
  update_assessments_scores
} from "@/utils/global";
import { SiteDirections } from "@/store/modules/common/interfaces";
import {
  APP_ASSESSMENTS,
  APP_ASSESSMENT_RESULT_PDF,
  SITE_DIRECTION
} from "@/store/modules/common/constants";
import { FETCH_APP_AI_ASSESSMENT } from "@/store/modules/recruiter/constants";
import { get_color_by_name } from "@/utils/colors";
import AppResourseNotFound from "@/components/candidate/app_assessments/AppResourseNotFound.vue";
import AssessmentResultLoading from "@/components/candidate/app_assessments/AssessmentResultLoading.vue";
import { ROOT_ERROR, ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import AiEvaluationCard from "@/components/recruiter/app_assessment/AiEvaluationCard.vue";
import GoBackHeaderPercentage from "@/components/recruiter/app_assessment/GoBackHeaderPercentage.vue";
export default Vue.extend({
  name: "AppAiReportDetails",
  components: {
    AppResourseNotFound,
    AssessmentResultLoading,
    AiEvaluationCard,
    GoBackHeaderPercentage
  },
  data() {
    return {
      loading: true,
      data_error: "",
      data: null as AppAssessments.AiAssessmentResults | null,
      is_assessment_details: false,
      download_report_loading: false,
      writing_assessment_details: [],
      mcqs_assessment_details: []
    };
  },
  beforeRouteEnter(to, from, next) {
    // AI assessments don't have version parameter, so allow navigation
    next();
  },
  beforeRouteUpdate(to, from, next) {
    // AI assessments don't have version parameter, so allow navigation
    next();
  },
  computed: {
    ...mapGetters("common", {
      app_assessments: APP_ASSESSMENTS,
      get_site_direction: SITE_DIRECTION
    }),
    SiteDirections() {
      return SiteDirections;
    }
  },
  async mounted() {
    await this.init();
  },
  methods: {
    update_assessments_scores,
    get_color_by_name,
    convert_seconds_to_minutes,
    ...mapActions("recruiter", {
      fetch_app_ai_assessment_action: FETCH_APP_AI_ASSESSMENT
    }),
    ...mapActions("common", {
      fetch_assessment_pdf: APP_ASSESSMENT_RESULT_PDF
    }),
    ...mapMutations({
      root_error: ROOT_ERROR,
      root_notification: ROOT_NOTIFICATION
    }),
    shortQuestionsLength() {
      return (
        this?.data?.result?.details?.filter(
          (detail: any) => detail.question.question_type === "text"
        ).length || 0
      );
    },
    mcqLength() {
      return (
        this?.data?.result?.details?.filter(
          (detail: any) => detail.question.question_type === "mcq"
        )?.length || 0
      );
    },
    trueFalseLength() {
      return (
        this?.data?.result?.details?.filter(
          (detail: any) => detail.question.question_type === "true_false"
        )?.length || 0
      );
    },
    async init() {
      this.loading = true;
      this.data_error = "";
      console.log("init");
      const assessment_id = parseStrToInt(this.$route.params.id);
      const user_id = parseStrToInt(this.$route.params.user_id);
      if (!assessment_id || !user_id) {
        this.data_error = `${this.$t("assessments.not-found")}`;
        this.loading = false;
        return;
      }

      // Fetching AI assessment details from server using assessment_id and user_id
      const ok = await this.fetch_app_ai_assessment_action({
        assessment_id,
        user_id
      });

      if (!ok) {
        this.data_error = `${this.$t("assessments.not-found")}`;
        this.loading = false;
        return;
      }
      // Read from store getter
      const payload =
        this.$store.getters[`recruiter/${FETCH_APP_AI_ASSESSMENT}`] || ok;
      console.log("payload", payload);
      if (!payload) {
        this.data_error = `${this.$t("assessments.not-found")}`;
        this.loading = false;
        return;
      }
      if (
        payload &&
        (payload.status === AppAssessments.AppAssessmentStatus.FAILED ||
          payload.status === AppAssessments.AppAssessmentStatus.IN_PROGRESS)
      ) {
        this.data_error = `${this.$t("assessments.assessment-failed")}`;
        this.loading = false;
        return;
      }
      this.data = payload; // Setting data
      this.loading = false;
    },
    /**
     * Get questions count by filter type (short or long)
     * @param filter {AppAssessments.QuestionFilter} - filter type
     */
    get_questions_count() {
      if (!this.data) return "";
      try {
        const details = this.data.result?.details;
        return details?.length || 0;
      } catch (e) {
        return "";
      }
    },
    open_details_dialog() {
      if (this.data && this.data.urls) {
        this.is_assessment_details = true;
      } else {
        this.root_notification(
          `${this.$t("assessments.recording-processing").toString()}`
        );
      }
    },
    get_answers_by_type() {
      return [];
    },
    // Function to download report
    async download_report() {
      if (!this.data) return;
      this.download_report_loading = true;
      const pdf = this.data.urls?.report as string | undefined;
      if (!pdf) {
        // Call backend API to get report
        const assessmentId = this.data.assessment?.id;
        const ids = this.data as { user_id?: number; user?: { id?: number } };
        const userId = ids.user_id || ids.user?.id;
        if (!assessmentId || !userId) {
          this.root_error(`${this.$t("assessments.pdf-report-failed")}`);
          this.download_report_loading = false;
          return;
        }
        const res = await this.fetch_assessment_pdf({
          assessment_id: assessmentId,
          user_id: userId
        });
        if (!res) {
          this.root_error(`${this.$t("assessments.pdf-report-failed")}`);
          this.download_report_loading = false;
          return;
        }
        // Handle blob response (development mode) or URL string (production mode)
        if (res instanceof Blob) {
          // Create a blob URL and download it
          const blobUrl = window.URL.createObjectURL(res);
          const filename = `assessment_result_${assessmentId}_${userId}.pdf`;
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = filename;
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          // Clean up the blob URL after download
          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(a);
          }, 100);
        } else if (
          typeof res === "string" &&
          (res.includes("http") || res.includes("https"))
        ) {
          // Production mode: res is a URL string
          await download_file(res);
        } else {
          this.root_error(`${this.$t("assessments.pdf-report-failed")}`);
        }
      } else {
        await download_file(pdf);
      }
      this.download_report_loading = false;
    },
    get_translation(key: string) {
      return this.$t(`assessments.${key}`);
    },
    get_name(data: { user?: { first_name?: string; last_name?: string } }) {
      if (data.user)
        return (
          (data.user?.first_name || "") + " " + (data.user?.last_name || "")
        );
      return "";
    }
  }
});
